import React from "react";
import { Box } from "@mui/material";

/**
 * Sparkline
 * props:
 *   data     – number[] (12 values); past months = actuals, future = forecast
 *   splitAt  – number  index where the line switches from solid → dotted (= currentMonth)
 *              if omitted the entire line is solid
 *   color    – stroke colour for the solid (actual) segment
 *   forecastColor – stroke colour for the dotted (forecast) segment; defaults to color
 */
const Sparkline = ({ data, splitAt, color = "#4CAF50", forecastColor }) => {
  const vals = (data || []).filter((v) => v != null && !isNaN(v));
  if (vals.length < 2) return <Box sx={{ width: 80, height: 28 }} />;

  const W = 80,
    H = 28;
  const len = data.length;

  const max = Math.max(...vals) || 1;
  const min = Math.min(...vals);
  const range = max - min || 1;

  const toX = (i) => ((i / (len - 1)) * W).toFixed(1);
  const toY = (v) => (H - ((v - min) / range) * (H - 4) - 2).toFixed(1);

  // Find last non-null index in the actual (solid) segment
  const split = splitAt != null ? splitAt : len; // exclusive end of solid segment

  // Solid segment: indices 0 … split-1 (skip nulls)
  const solidPts = [];
  for (let i = 0; i < Math.min(split, len); i++) {
    if (data[i] != null) solidPts.push(`${toX(i)},${toY(data[i])}`);
  }

  // Dotted segment: starts from the last solid point → all forecast points
  // Include the last actual point as the bridge start so the lines connect
  const dottedPts = [];
  if (split < len) {
    // Bridge: last actual point
    const lastSolidIdx = [...data.slice(0, split)]
      .map((v, i) => (v != null ? i : -1))
      .filter((i) => i >= 0)
      .pop();
    if (lastSolidIdx != null)
      dottedPts.push(`${toX(lastSolidIdx)},${toY(data[lastSolidIdx])}`);

    for (let i = split; i < len; i++) {
      if (data[i] != null) dottedPts.push(`${toX(i)},${toY(data[i])}`);
    }
  }

  const fc = forecastColor || color;

  return (
    <svg width={W} height={H} style={{ overflow: "visible" }}>
      {solidPts.length >= 2 && (
        <polyline
          points={solidPts.join(" ")}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      )}
      {dottedPts.length >= 2 && (
        <polyline
          points={dottedPts.join(" ")}
          fill="none"
          stroke={fc}
          strokeWidth="1.5"
          strokeDasharray="3,2"
          strokeLinejoin="round"
          strokeLinecap="round"
          opacity={0.85}
        />
      )}
    </svg>
  );
};

export default Sparkline;
